import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  CATEGORY_NAME,
  DESCRIPTION,
  SUB_CATEGORY,
  SUB_CATEGORY_DESC,
  SUB_CATEGORY_NAME,
} from "./consts";
import { useNavigate, useParams } from "react-router-dom";
import { addSchema, AddCategoryFormValues } from "./validations";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useAddTaskCategoryMutation, useGetTaskCategoriesById, useUpdateTaskCategoryMutation, useAddTaskSubCategoryMutation, useGetTaskSubCategoriesByCategoryId, useDeleteTaskSubCategoryMutation } from "@/services";
import { toast } from "@/components";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect, useState } from "react";

const useAdd = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id, action, tabRowId, tabRowAction } = useParams();
  const isViewCategory = tabRowAction == "view";
  const isViewProject = action == "view";
  const [deletedSubCategoryIds, setDeletedSubCategoryIds] = useState<string[]>([])

  const isView = isViewCategory || isViewProject;

  const projectData = useSelector(
    (state: RootState) => state.currentData.projectData
  );

  const form = useForm<AddCategoryFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [CATEGORY_NAME]: "",
      [DESCRIPTION]: "",
      [SUB_CATEGORY]: [{ [SUB_CATEGORY_NAME]: "", [SUB_CATEGORY_DESC]: "" }],
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const navigateUrl = id
    ? `/${routes.admin}/${routes.projectOverview}/${id}/${action}/${routes.taskCategory}`
    : `/${routes.admin}/${routes.projectOverview}/${routes.add}/${routes.taskCategory}`;

  const breadcrumbData = [
    {
      link: navigateUrl,
      name: `Category`,
    },
    {
      link: "",
      name: "Add",
    },
  ];

  const onBackClick = () => {
    navigate(navigateUrl);
  };

  const {data: categoryData, isLoading: isLoadingCategoryData} = useGetTaskCategoriesById({
    id: tabRowId as string,
    queryParams: {
      enabled: !!tabRowId
    }
  })

  const {data: subCategoryList, isLoading: isLoadingSubcategory} = useGetTaskSubCategoriesByCategoryId({
    categoryId: tabRowId as string,
    queryParams: {
      enabled: !!tabRowId
    }
  })


  useEffect(() => {
    if (categoryData?.data) {
      form.setValue(CATEGORY_NAME, categoryData?.data?.[CATEGORY_NAME] || '')
      form.setValue(DESCRIPTION, categoryData?.data?.[DESCRIPTION] || '')
    }
  }, [categoryData]);

  useEffect(() => {
    if (subCategoryList?.data) {
      form.setValue(SUB_CATEGORY, subCategoryList?.data)
    }
  }, [subCategoryList])


  const {
    mutateAsync: deleteSubCategoryInfo,
    isPending: isDeleteSubCategoryPending,
  } = useDeleteTaskSubCategoryMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateTaskCategoryMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: async (data: any) => {
        try {
          const additionalPromises: Promise<any>[] = [];

          form.watch(SUB_CATEGORY)?.map((val: any) => {
            if (!val?.taskSubCategoryId) {
              additionalPromises.push(
                addSubCategoryInfo({
                  data: {
                    ...val,
                    projectId: id as string,
                    taskCategoryId: data?.data?.data?.taskCategoryId,
                  },
                })
              );
            }
          });

          deletedSubCategoryIds?.map((val: any) => {
            additionalPromises.push(
              deleteSubCategoryInfo({id: val})
            )
          })

          await Promise.all(additionalPromises);

          toast({
            message: "Category and Sub-Category Updated Successfully",
            type: "success",
          });

          queryClient.invalidateQueries({
            queryKey: ["task-categories", projectData?.projectId],
          });
          onBackClick();
        } catch (error) {
          console.error("Error in post-success APIs:", error);
          toast({
            message: "Something went wrong while processing additional data.",
            type: "error",
          });
        }
      },
    });
    
  const {
    mutateAsync: addSubCategoryInfo,
    isPending: isAddSubCategoryPending,
  } = useAddTaskSubCategoryMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
  });

  const { mutate: addInfo, isPending: isAddPending } =
    useAddTaskCategoryMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: async (data: any) => {
        try {
          const additionalPromises: Promise<any>[] = [];

          form.watch(SUB_CATEGORY)?.map((val: any) => {
            additionalPromises.push(
              addSubCategoryInfo({
                data: {
                  ...val,
                  projectId: id as string,
                  taskCategoryId: data?.data?.data?.taskCategoryId,
                },
              })
            );
          });

          await Promise.all(additionalPromises);

          toast({
            message: "Category and Sub-Category Added Successfully",
            type: "success",
          });

          queryClient.invalidateQueries({
            queryKey: ["task-categories", projectData?.projectId],
          });
          onBackClick();
        } catch (error) {
          console.error("Error in post-success APIs:", error);
          toast({
            message: "Something went wrong while processing additional data.",
            type: "error",
          });
        }
      },
    });

  const onSubmit = (data: AddCategoryFormValues) => {
    let _data = setEmptyFieldsToNull(data);
    _data = removeEmptyFields(_data);

    if (tabRowId) {
      updateInfo({id: tabRowId, data: { ..._data, projectId: projectData?.projectId } })
    } else {
      addInfo({ data: { ..._data, projectId: projectData?.projectId } });
    }
  };

  const onAddMoreSubCategory = () => {
    form.clearErrors();
    form.setValue(SUB_CATEGORY, [
      ...form.getValues()[SUB_CATEGORY],
      { [SUB_CATEGORY_NAME]: "", [SUB_CATEGORY_DESC]: "" },
    ]);
  };

  const onDeleteSubCategory = (idx: number) => () => {
    const taskSubCategoryId = (form.getValues()[SUB_CATEGORY][idx] as any)?.taskSubCategoryId

    if (taskSubCategoryId) {
      setDeletedSubCategoryIds((prev: any) => [...prev, taskSubCategoryId])
    }

    form.setValue(SUB_CATEGORY, [
      ...form
        .getValues()
        [SUB_CATEGORY]?.filter((_: any, index: number) => idx != index),
    ]);
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    onAddMoreSubCategory,
    onDeleteSubCategory,
    isAddSubCategoryPending,
    isView,
    isLoadingCategoryData,
    isLoadingSubcategory,
    isDeleteSubCategoryPending,
    isUpdatePending
  };
};

export default useAdd;
